import { t } from "@travily/i18n";
import { DropMenu, DropMenuItem, DropMenuPopover, DropMenuSeparator } from "@ui/components/DropMenu";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { usePlatform } from "@ui/hooks/use-platform";
import { firstSelectedKey } from "@ui/lib/selection";
import { MenuTrigger } from "@ui/primitives/Menu";
import type { PressEvent, Selection } from "@ui/primitives/shared";
import { cloneElement, type ReactElement, useState } from "react";
import { ActionSheetSurface } from "./ActionSheetSurface";
import { type ActionMenuEntry, type ActionMenuOption, isSeparator } from "./types";

type TriggerElement = ReactElement<{ onPress?: (event: PressEvent) => void; "aria-label"?: string }>;

type ActionMenuPlatform = "ios" | "android" | "desktop";

export interface ActionMenuProps {
  /** The button that opens the menu. A React Aria button (e.g. `OutlineButton`). */
  trigger: TriggerElement;
  /** Falsy entries are dropped, so callers can inline conditionals. */
  items: ReadonlyArray<ActionMenuEntry | false | null | undefined>;
  /** Desktop popover placement (no effect on the mobile sheet). */
  placement?: "bottom" | "bottom start" | "bottom end";
  /** Optional non-interactive header on the mobile sheet. */
  title?: string;
  /** Accessible name for the mobile sheet; defaults to the trigger's `aria-label`. */
  label?: string;
  onOpenChange?: (open: boolean) => void;
  selectionMode?: "single";
  selectedKey?: string;
  onSelectionChange?: (key: string) => void;
  /** iOS Cancel pill label. Defaults to `common.actions.cancel`. */
  cancelLabel?: string;
  /** @internal Storybook/testing — forces the resolved platform. */
  platformOverride?: ActionMenuPlatform;
}

/**
 * Platform-adaptive `...` menu. On desktop (`>=md`) it renders the existing
 * dropdown popover unchanged; on mobile it presents a native-style action sheet
 * (iOS alert-style or Material 3, chosen via `usePlatform`). The same `items`
 * model drives both. Platform/breakpoint resolution mirrors the system `Dialog`.
 *
 * When to use this vs `Dialog` vs `PersistentSheet`: see Foundations/Overlays
 * in Storybook (`src/docs/Overlays.mdx`).
 */
export function ActionMenu({
  trigger,
  items,
  placement = "bottom end",
  title,
  label,
  onOpenChange,
  selectionMode,
  selectedKey,
  onSelectionChange,
  cancelLabel,
  platformOverride,
}: ActionMenuProps) {
  const isDesktop = useBreakpoint("md");
  const os = usePlatform();
  const platform: ActionMenuPlatform = platformOverride ?? (isDesktop ? "desktop" : os === "ios" ? "ios" : "android");

  const entries = items.filter((entry): entry is ActionMenuEntry => Boolean(entry));

  if (platform === "desktop") {
    return (
      <DesktopActionMenu
        entries={entries}
        onOpenChange={onOpenChange}
        onSelectionChange={onSelectionChange}
        placement={placement}
        selectedKey={selectedKey}
        selectionMode={selectionMode}
        trigger={trigger}
      />
    );
  }

  return (
    <MobileActionMenu
      cancelLabel={cancelLabel}
      entries={entries}
      label={label}
      onOpenChange={onOpenChange}
      onSelectionChange={onSelectionChange}
      platform={platform}
      selectedKey={selectedKey}
      selectionMode={selectionMode}
      title={title}
      trigger={trigger}
    />
  );
}

interface BranchProps {
  trigger: TriggerElement;
  entries: ActionMenuEntry[];
  onOpenChange?: (open: boolean) => void;
  selectionMode?: "single";
  selectedKey?: string;
  onSelectionChange?: (key: string) => void;
}

function singleSelectionProps(
  selectionMode: "single" | undefined,
  selectedKey: string | undefined,
  onSelectionChange: ((key: string) => void) | undefined,
) {
  if (selectionMode !== "single") return {};
  return {
    selectionMode: "single" as const,
    selectedKeys: selectedKey ? new Set([selectedKey]) : new Set<string>(),
    onSelectionChange: (keys: Selection) => {
      const key = firstSelectedKey(keys);
      if (key !== undefined) onSelectionChange?.(key);
    },
  };
}

function DesktopActionMenu({
  trigger,
  entries,
  placement,
  onOpenChange,
  selectionMode,
  selectedKey,
  onSelectionChange,
}: BranchProps & { placement: "bottom" | "bottom start" | "bottom end" }) {
  // Key separators by the preceding option's id (stable across reorders) so we
  // never fall back to array indices.
  let lastOptionId = "start";
  const keyed = entries
    .filter((entry) => isSeparator(entry) || !entry.mobileOnly)
    .map((entry) => {
      if (isSeparator(entry)) return { entry, key: `separator-after-${lastOptionId}` };
      lastOptionId = entry.id;
      return { entry, key: entry.id };
    });

  return (
    <MenuTrigger onOpenChange={onOpenChange}>
      {trigger}
      <DropMenuPopover placement={placement}>
        <DropMenu {...singleSelectionProps(selectionMode, selectedKey, onSelectionChange)}>
          {keyed.map(({ entry, key }) =>
            isSeparator(entry) ? (
              <DropMenuSeparator key={key} />
            ) : (
              <DropMenuItem
                key={key}
                href={entry.href}
                icon={entry.icon}
                id={entry.id}
                isDisabled={entry.isDisabled}
                onAction={entry.onAction}
                textValue={entry.label}
              >
                {entry.label}
              </DropMenuItem>
            ),
          )}
        </DropMenu>
      </DropMenuPopover>
    </MenuTrigger>
  );
}

function MobileActionMenu({
  trigger,
  entries,
  platform,
  title,
  label,
  cancelLabel,
  onOpenChange,
  selectionMode,
  selectedKey,
  onSelectionChange,
}: BranchProps & { platform: "ios" | "android"; title?: string; label?: string; cancelLabel?: string }) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setIsOpen(next);
    onOpenChange?.(next);
  };

  const options = entries.filter((entry): entry is ActionMenuOption => !isSeparator(entry));

  const triggerProps = trigger.props;
  const resolvedLabel = label ?? title ?? triggerProps["aria-label"] ?? t("common.actions.more");
  const triggerEl = cloneElement(trigger, {
    onPress: (event: PressEvent) => {
      triggerProps.onPress?.(event);
      handleOpenChange(true);
    },
  });

  // The surface mounts its overlay subtree only while open (plus the exit
  // animation) — closed instances cost nothing, which matters where many
  // ActionMenus render at once (e.g. the PhotoOrganizer grid, TripCard lists).
  return (
    <>
      {triggerEl}
      <ActionSheetSurface
        cancelLabel={cancelLabel ?? t("common.actions.cancel")}
        label={resolvedLabel}
        onOpenChange={handleOpenChange}
        onSelectionChange={onSelectionChange}
        options={options}
        platform={platform}
        isOpen={isOpen}
        selectedKey={selectedKey}
        selectionMode={selectionMode}
        title={title}
      />
    </>
  );
}
